/**
 * ZOE thinking-state card — step-by-step investigation tracker.
 * Visual pattern from zoe_thinking_state.html (Variant A).
 */

import { useEffect, useState } from 'react';
import { AlertTriangle, Check, Clock, Loader2, Sparkles } from 'lucide-react';
import {
  thinkingProgressPercent,
  type ThinkingStep,
  type ThinkingStepStatus,
  type ZoeThinkingModel,
} from './zoeThinkingModel';

interface ZoeThinkingStateProps {
  model: ZoeThinkingModel;
  /** Epoch ms when the current investigation started. */
  startedAt: number;
}

function StepDot({ status }: { status: ThinkingStepStatus }) {
  if (status === 'done') {
    return (
      <div className="relative z-raised mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success/15 text-success">
        <Check className="h-3 w-3" strokeWidth={3} />
      </div>
    );
  }
  if (status === 'running') {
    return (
      <div className="relative z-raised mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ring/15 text-ring">
        <span className="zoe-thinking-pulse-ring" aria-hidden />
        <Loader2 className="h-3 w-3 animate-spin" />
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="relative z-raised mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-warning/10 text-warning">
        <AlertTriangle className="h-3 w-3" />
      </div>
    );
  }
  return (
    <div className="relative z-raised mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-white/15 text-accent">
      <span className="h-1.5 w-1.5 rounded-full bg-accent" />
    </div>
  );
}

function titleClass(status: ThinkingStepStatus): string {
  if (status === 'running') return 'text-ring';
  if (status === 'pending') return 'text-accent';
  if (status === 'error') return 'text-warning';
  return 'text-foreground';
}

function ThinkingStepRow({ step, isLast }: { step: ThinkingStep; isLast: boolean }) {
  return (
    <div className="relative flex items-start gap-3 py-2">
      {!isLast ? (
        <span
          className="absolute left-3 top-8 -bottom-0.5 w-px bg-white/15"
          aria-hidden
        />
      ) : null}
      <StepDot status={step.status} />
      <div className="min-w-0 flex-1 pt-0.5">
        <div className={`text-sm leading-snug ${titleClass(step.status)}`}>{step.label}</div>
        {step.meta && step.status !== 'pending' ? (
          <div
            className={`mt-0.5 text-xs leading-snug ${
              step.status === 'error' ? 'text-warning/80' : 'text-muted-foreground'
            }`}
          >
            {step.meta}
          </div>
        ) : null}
        {step.status === 'running' && step.tool ? (
          <div className="mt-1.5 inline-flex items-center gap-1 rounded border border-ring/25 bg-ring/10 px-2 py-0.5 font-mono text-xs text-ring">
            {step.tool.replace(/^profit_agent\//, '')}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function ZoeThinkingState({ model, startedAt }: ZoeThinkingStateProps) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (model.finished) return;
    const id = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(id);
  }, [model.finished]);

  const elapsedSec = Math.max(0, (now - startedAt) / 1000);
  const elapsedLabel = `${elapsedSec.toFixed(1)}s`;
  const progress = thinkingProgressPercent(model, elapsedSec);

  return (
    <div className="rounded-xl border border-white/[0.14] bg-background px-5 pb-4 pt-5">
      <div className="mb-5 flex items-center gap-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ring/15">
          <Sparkles className="h-3.5 w-3.5 text-ring" />
        </div>
        <span className="flex-1 text-sm font-medium text-foreground">{model.headerLabel}</span>
        <span className="text-xs text-muted-foreground tabular-nums">{elapsedLabel}</span>
      </div>

      <div className="flex flex-col">
        {model.steps.map((step, i) => (
          <ThinkingStepRow
            key={step.id}
            step={step}
            isLast={i === model.steps.length - 1}
          />
        ))}
      </div>

      <div className="mt-4 h-0.5 overflow-hidden rounded-sm bg-white/15">
        <div
          className="h-0.5 rounded-sm bg-ring transition-width duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {model.warningNotice ? (
        <div className="mt-3.5 rounded-r-lg border-l-2 border-warning/30 bg-warning/10 px-3 py-2 text-xs leading-relaxed text-warning">
          {model.warningNotice}
        </div>
      ) : null}

      <div className="mt-3 flex items-center gap-1.5 border-t border-white/[0.07] pt-3 text-xs text-muted-foreground">
        <Clock className="h-3 w-3 shrink-0" />
        <span className="tabular-nums">{elapsedLabel} elapsed</span>
      </div>
    </div>
  );
}
